// src/Api.js
import axios from 'axios'; // Ensure axios is imported if not already
import { getLogoutRedirectUrl } from './utils/authUtils';

const isLocalhost =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

export const API_BASE_URL = isLocalhost
  ? "http://127.0.0.1:8000"          // local FastAPI
  : "https://cctv-app.whitetiger.ai";     // production backend

export const PREDEFINED_MODES_MAP = {
  "Customer Service Monitor":"Audio Sentiment Analysis",
  "Traffic Analyzer":        "Vehicle Counting & LP Rec",
  "Footfall Tracker":        "People Counting",
  "Queue Monitor":           "Queue Management",
  "Idle Employee Tracker":   "Idle Employee Counter",
  "Waiting Time Tracker": "Waiting Time Tracker",
  "Bounce Detector": "Bounce Audio Analysis",
 
};


async function handleApiResponse(res, op = "API") {
  const asyncStartOk =
    res.status === 202 &&
    (res.url.includes("/process/start_rtsp_audio") ||
     res.url.includes("/analyze_audio/") ||
     res.url.includes("/process/request_websocket_audio_slot") || // ADDED for WebSocket slot
     res.url.includes("/process/start_bounce_rtsp_audio") ||
     res.url.includes("/analyze_bounce_audio/") ||
     res.url.includes("/mics/request_stream") ||                  // NEW
     res.url.includes("/mics/stop_stream")                        // NEW
    );

  const multiInstanceStartOk = res.status === 207 && res.url.includes("/process/start");

  // 🔒 SECURITY: Handle 401 Unauthorized - Session expired or invalid token
  if (res.status === 401) {
    console.error(`[Api] ${op} failed: Authentication required (401). Redirecting to login...`);

    // Clear invalid token from localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('organizationId');
      localStorage.removeItem('userRoles');
    }

    // Redirect to login page (landing page)
    if (typeof window !== 'undefined') {
      // Store current URL to redirect back after login
      const currentPath = window.location.pathname + window.location.search;
      localStorage.setItem('redirectAfterLogin', currentPath);

      window.location.href = getLogoutRedirectUrl();
    }

    const err = new Error('Session expired. Please login again.');
    err.status = 401;
    throw err;
  }

  // 🔒 SECURITY: Handle 403 Forbidden - Insufficient permissions
  if (res.status === 403) {
    let detail = 'Access denied. You do not have permission for this action.';
    try {
      const errorData = await res.json();
      detail = errorData.detail || detail;
    } catch (_) {
      // Use default message if JSON parsing fails
    }

    console.error(`[Api] ${op} failed: Forbidden (403) - ${detail}`);
    const err = new Error(detail);
    err.status = 403;
    throw err;
  }

  if (!res.ok && !asyncStartOk && !multiInstanceStartOk) {
    let detail = `HTTP ${res.status}`;
    let errorData = {};
    try {
        errorData = await res.json();
        detail = errorData.detail || detail;
    } catch (_) {
        try { detail = await res.text(); } catch {}
    }

    console.error(`[Api] ${op} failed (${res.url}): ${detail}`);
    const err = new Error(`${op} failed: ${detail}`);
    err.status = res.status;
    err.data = errorData; // Attach full error data object
    throw err;
  }

  if (res.status === 204) return { message: `${op} succeeded (no content)` };

  try {
    const data = await res.json();
    
    // Enhanced logging for debugging
    console.log(`[Api] ${op} ok (Status ${res.status}):`, {
      dataType: typeof data,
      isArray: Array.isArray(data),
      hasItems: data && typeof data === 'object' && 'items' in data,
      itemsType: data && data.items ? typeof data.items : 'N/A',
      itemsIsArray: data && Array.isArray(data.items),
      dataKeys: data && typeof data === 'object' ? Object.keys(data) : 'N/A',
      url: res.url
    });
    
    if (multiInstanceStartOk && data.instances) {
        console.log(`[Api] ${op} (Multi-Status ${res.status}):`, data.overall_message);
        data.instances.forEach(inst => {
            console.log(`  Instance ${inst.instance_id} (${inst.camera_id} - ${inst.mode}): ${inst.status} - ${inst.message}`);
        });
    }
    
    return data;
  } catch (e) {
    console.error(`[Api] ${op}: JSON parsing failed for status ${res.status}. Error:`, e, 'URL:', res.url);
    
    if (asyncStartOk || multiInstanceStartOk) {
        console.warn(`[Api] ${op}: async/multi-status (status ${res.status}) but failed to parse JSON. Error: ${e.message}. URL: ${res.url}`);
        return { message: `${op} started (status ${res.status}, but response body was not valid JSON or empty)`, _asyncStartNoJsonBody: true };
    }
    
    const textResponse = await res.text().catch(() => `Non-JSON response, could not read text body.`);
    console.warn(`[Api] ${op}: non-JSON response for status ${res.status}. Error: ${e.message}. Body: ${textResponse.substring(0,100)}...`);
    return { message: `${op} succeeded (status ${res.status}), but response was not JSON: ${textResponse.substring(0,100)}...` };
  }
}

export const uploadVideo = async (fileOrUrl) => {
  const body = new FormData();
  if (fileOrUrl instanceof File) { body.append("file", fileOrUrl); }
  else if (typeof fileOrUrl === "string") { body.append("url", fileOrUrl.trim()); }
  else { throw new Error("uploadVideo: argument must be File or URL string"); }
  const r = await fetch(`${API_BASE_URL}/upload/video`, { method: "POST", body });
  return handleApiResponse(r, "Video upload");
};

export const startProcessing = async (payload) =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/process/start`,
      buildAuthFetchOptions({
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      })
    ),
    "Start Processing (Multiple Instances Possible)"
  );

export const stopSpecificInstance = async (instanceId) =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/process/stop_instance`,
      buildAuthFetchOptions({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instance_id: instanceId }),
      })
    ),
    `Stop Specific Instance (${instanceId.slice(-8)})`
  );

export const stopAllProcessingInstances = async () =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/process/stop_all`,
      buildAuthFetchOptions({ method: "POST" })
    ),
    "Stop All Processing Instances"
  );

export const getAllInstancesStatus = async () =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/process/status`,
      buildAuthFetchOptions()
    ),
    "Get All Instances Status"
  );

export const getLineCalibration = async (modeSlug, sourcePath) =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/calibration/${modeSlug}?source_path=` + encodeURIComponent(sourcePath),
      buildAuthFetchOptions()
    ),
    `Get ${modeSlug} calibration`
  );

export const saveLineCalibration = async (modeSlug, sourcePath, payload) =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/calibration/${modeSlug}?source_path=` + encodeURIComponent(sourcePath),
      buildAuthFetchOptions({
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      })
    ),
    `Save ${modeSlug} calibration`
  );

export const submitQuestion = async ({ name, email, question }) =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/ask_question`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ firstName: name, email, problem: question }),
    }), "Submit question"
  );

export const analyzeAudio = async ({ file_to_upload, s3_http_url, force_chunked_processing = false, camera_location }) => {
  const body = new FormData();
  let operationName = "Analyze Audio";
  if (file_to_upload instanceof File) { 
    body.append("file", file_to_upload, file_to_upload.name); 
    operationName = "Start Local Audio Chunk Processing";
  }
  else if (typeof s3_http_url === 'string' && s3_http_url.trim()) {
    body.append("s3_uri", s3_http_url.trim());
    if (force_chunked_processing) { 
      body.append("process_as_chunked", "true"); 
      operationName = "Start Remote Audio Chunk Processing (via URL)"; 
    }
    else { operationName = "Analyze Remote Audio (S3/HTTP - Single Pass)"; }
  } else { throw new Error("analyzeAudio: Must provide either 'file_to_upload' or 's3_http_url'.");}
  
  if (camera_location) {
    body.append("camera_location", camera_location);
  }

  const r = await fetch(`${API_BASE_URL}/analyze_audio/`, { method: "POST", body });
  return handleApiResponse(r, operationName);
};

export const startRtspAudioProcessing = async (payload) =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/process/start_rtsp_audio`,
      buildAuthFetchOptions({
        method: "POST",
        headers: {"Content-Type":"application/json"},
        body:JSON.stringify({
          ...payload,
          // Add analysis_mode with default fallback for backward compatibility
          analysis_mode: payload.analysis_mode || "Customer_sentiment_analyzer"
        })
      })
    ),
    "Start RTSP Audio Processing"
  );

export const requestWebSocketAudioSlot = async (payload) =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/process/request_websocket_audio_slot`,
      buildAuthFetchOptions({
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...payload,
          analysis_mode:
            // ➊  the new required field
            payload.analysis_mode ??
            // ➋  fallback – keep old callers working
            (payload.modeIdentifier === "Bounce Detector"
                ? "Bounce Detector"
                : "Customer Service Monitor"),
        }),
      })
    ),
    "Request WebSocket Audio Slot"
  );

// export const stopAudioStream = async (streamId) =>
//   handleApiResponse(
//     await fetch(`${API_BASE_URL}/process/stop_audio_stream?stream_id=`+encodeURIComponent(streamId),{method:"POST"}),
//     `Stop Audio Stream (${streamId})`
//   );

export const stopAudioStream = async (streamId, processor = "all") =>
  handleApiResponse(
    await fetch(
      `${API_BASE_URL}/process/stop_audio_stream?stream_id=` +
        encodeURIComponent(streamId) +
       `&processor=${processor}`,
      buildAuthFetchOptions({ method: "POST" })
    ),
    `Stop Audio Stream (${streamId}, ${processor})`
  );
export const listAllAudioTasks = async () =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/process/all_audio_tasks_status`,
      buildAuthFetchOptions()
    ),
    "List All Audio Tasks Status"
  );

export const getVideoLogEventSource = (instanceId) => {
  if (!instanceId) return null;
  const token = getAccessToken();
  if (!token) {
    console.warn('[Api] getVideoLogEventSource: No access token available');
    return null;
  }
  return new EventSource(`${API_BASE_URL}/process/instance_log_stream/${instanceId}?token=${encodeURIComponent(token)}`);
};

export const getLiveVehicleCountEventSource = () => {
  if (!LIVE_COUNT_SSE_CONFIG["Traffic Analyzer"]) return null;
  const token = getAccessToken();
  if (!token) {
    console.warn('[Api] getLiveVehicleCountEventSource: No access token available');
    return null;
  }
  return new EventSource(`${API_BASE_URL}${LIVE_COUNT_SSE_CONFIG["Traffic Analyzer"].path}?token=${encodeURIComponent(token)}`);
};

export const getAudioLogEventSource = (audioStreamId, isBounce = false) => {
  if (!audioStreamId) return null;
  const token = getAccessToken();
  if (!token) {
    console.warn('[Api] getAudioLogEventSource: No access token available');
    return null;
  }
  const endpointPath = isBounce
    ? `/process/bounce_audio_log_stream/${audioStreamId}`
    : `/process/audio_log_stream/${audioStreamId}`;
  return new EventSource(`${API_BASE_URL}${endpointPath}?token=${encodeURIComponent(token)}`);
};

export const LIVE_COUNT_SSE_CONFIG = { 
  "Traffic Analyzer": { 
    path: "/process/live_vehicle_count_stream",
    eventName: "live_vehicle_count_update",
  },
};

const MODE_IDENTIFIER_TO_API_SLUG_MAP = {
  "Customer Service Monitor": "audio",
  "Traffic Analyzer": "vehicle",
  "Footfall Tracker": "footfall",
  "Queue Monitor": "queue",       
  "Idle Employee Tracker": "idle",
  "Waiting Time Tracker": "waiting_time",
  "Bounce Detector": "bounce",    

  "Audio": "audio",
  "Bounce": "bounce",
  "Footfall": "footfall",
  "Idle": "idle",
  "Vehicle": "vehicle",
  "Waiting_time": "waiting_time",

  "audio": "audio",
  "bounce": "bounce",
  "footfall": "footfall",
  "idle": "idle",
  "vehicle": "vehicle",
  "queue": "queue",
  "waiting_time": "waiting_time", 
};

export const getHistoricalLogs = async (modeIdentifier, sourceId, startTimeUtc, endTimeUtc, limit = 100, skip = 0, sortOrder = 'desc') => {
  if (!modeIdentifier || !sourceId) {
    console.warn("[Api.js] getHistoricalLogs: modeIdentifier or sourceId is missing.", { modeIdentifier, sourceId });
    return { items: [], total_count: 0, has_more: false, page: 0, page_size: limit, total_pages: 0 };
  }

  const apiMode = MODE_IDENTIFIER_TO_API_SLUG_MAP[modeIdentifier];

  if (!apiMode) {
    console.warn(`[Api.js] getHistoricalLogs: No backend API slug mapping for mode identifier "${modeIdentifier}". Cannot fetch logs.`);
    return { items: [], total_count: 0, has_more: false, page: 0, page_size: limit, total_pages: 0 };
  }

  const encodedSourceId = encodeURIComponent(sourceId);
  
  let queryParams = new URLSearchParams();
  if (startTimeUtc) {
    queryParams.append('startTimeUtc', startTimeUtc);
  }
  if (endTimeUtc) {
    queryParams.append('endTimeUtc', endTimeUtc);
  }
  if (limit !== null && limit !== undefined) {
    queryParams.append('limit', String(limit));
  }
  if (skip !== null && skip !== undefined) {
    queryParams.append('skip', String(skip));
  }
  if (sortOrder) {
    queryParams.append('sortOrder', sortOrder);
  }

  const queryString = queryParams.toString();
  // Using /logs/historical_by_date/ as per previous update, assuming sourceId is camera_display_name
  const requestUrl = `${API_BASE_URL}/logs/historical_by_date/${apiMode}/${encodedSourceId}${queryString ? `?${queryString}` : ''}`;
  
  console.log(`[Api.js] Fetching historical logs (by date): Identifier='${modeIdentifier}', API_Mode='${apiMode}', Source='${sourceId}', Limit='${limit}', Skip='${skip}', SortOrder='${sortOrder}', URL='${requestUrl}'`);

  try {
    const response = await fetch(requestUrl, buildAuthFetchOptions());
    const data = await handleApiResponse(response, `Get Historical Logs for ${modeIdentifier} by Date`);
    
    // Handle both new paginated response and legacy array response (backward compatibility)
    if (Array.isArray(data)) {
      console.log(`[Api.js] Received legacy array response for ${modeIdentifier}, converting to paginated format`);
      return {
        items: data,
        total_count: data.length,
        has_more: false,
        page: 0,
        page_size: data.length,
        total_pages: 1
      };
    }
    
    // New paginated response format
    return {
      items: data.items || [],
      total_count: data.total_count || 0,
      has_more: data.has_more || false,
      page: data.page || 0,
      page_size: data.page_size || limit,
      total_pages: data.total_pages || 0
    };
  } catch (error) { // handleApiResponse will throw, so this catch is for network/unhandled issues
    console.error(`[Api.js] Network/Unhandled error fetching logs for API_Mode='${apiMode}', source='${sourceId}':`, error);
    return { items: [], total_count: 0, has_more: false, page: 0, page_size: limit, total_pages: 0 };
  }
};

export const sendEmailAlert = async (alertDetails) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/alerts/send`, alertDetails);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.detail || error.message || "Failed to send alert";
    console.error("Error sending email alert:", errorMessage, error.response || error);
    throw new Error(errorMessage); // Re-throw to be caught by calling component
  }
};

export const analyzeBounceAudio = async ({ file_to_upload, s3_http_url, force_chunked_processing = false, camera_location }) => {
  const body = new FormData();
  let operationName = "Analyze Bounce Audio";
  if (file_to_upload instanceof File) {
    body.append("file", file_to_upload, file_to_upload.name);
    operationName = "Start Local Bounce Audio Chunk Processing";
  } else if (typeof s3_http_url === 'string' && s3_http_url.trim()) {
    body.append("s3_uri", s3_http_url.trim());
    if (force_chunked_processing) {
      body.append("process_as_chunked", "true");
      operationName = "Start Remote Bounce Audio Chunk Processing (via URL)";
    } else {
      operationName = "Analyze Remote Bounce Audio (S3/HTTP - Single Pass)";
    }
  } else {
    throw new Error("analyzeBounceAudio: Must provide either 'file_to_upload' or 's3_http_url'.");
  }
  
  if (camera_location) {
    body.append("camera_location", camera_location);
  }
  
  const r = await fetch(`${API_BASE_URL}/analyze_bounce_audio/`, { method: "POST", body });
  return handleApiResponse(r, operationName);
};

export const startRtspBounceAudioProcessing = async (payload) =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/process/start_bounce_rtsp_audio`,
      buildAuthFetchOptions({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
    ),
    "Start RTSP Bounce Audio Processing"
  );

export const stopBounceAudioStream = async (streamId) =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/process/stop_bounce_audio_stream?stream_id=` + encodeURIComponent(streamId),
      buildAuthFetchOptions({
        method: "POST",
      })
    ),
    `Stop Bounce Audio Stream (${streamId})`
  );

export const listAllBounceAudioTasks = async () =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/process/all_bounce_audio_tasks_status`,
      buildAuthFetchOptions()
    ),
    "List All Bounce Audio Tasks Status"
  );

export const getBounceAudioLogEventSource = (audioStreamId) => {
  return getAudioLogEventSource(audioStreamId, true);
};

export const getLogModes = async () => {
  try {
    const response = await handleApiResponse(
      await fetch(`${API_BASE_URL}/logs/modes`, buildAuthFetchOptions()),
      "Get Log Modes"
    );
    
    // Validate response structure
    if (!response || !Array.isArray(response.modes)) {
      console.warn('[Api.js] getLogModes: Invalid response structure, expected {modes: Array}', response);
      return { modes: [] };
    }
    
    return response;
  } catch (error) {
    console.error('[Api.js] getLogModes error:', error);
    return { modes: [] };
  }
};

export const getLogSourcesForMode = async (modeSlug) => {
  if (!modeSlug) {
    console.warn("getLogSourcesForMode: modeSlug is missing");
    return { mode: modeSlug, sources: [] }; 
  }

  try {
    const response = await handleApiResponse(
      await fetch(`${API_BASE_URL}/logs/sources/${modeSlug}`, buildAuthFetchOptions()),
      `Get Log Sources for ${modeSlug}`
    );
    
    // Validate response structure
    if (!response || !Array.isArray(response.sources)) {
      console.warn(`[Api.js] getLogSourcesForMode: Invalid response structure for mode ${modeSlug}, expected {sources: Array}`, response);
      return { mode: modeSlug, sources: [] };
    }
    
    return response;
  } catch (error) {
    console.error(`[Api.js] getLogSourcesForMode error for mode ${modeSlug}:`, error);
    return { mode: modeSlug, sources: [] };
  }
};

export const getHistoricalLogsByDate = async (modeIdentifier, sourceId, startTimeUtc, endTimeUtc, limit = 100, skip = 0, sortOrder = 'desc') => {
  if (!modeIdentifier || !sourceId) {
    console.warn("[Api.js] getHistoricalLogsByDate: modeIdentifier or sourceId is missing.", { modeIdentifier, sourceId });
    return { items: [], total_count: 0, has_more: false, page: 0, page_size: limit, total_pages: 0 };
  }

  const apiMode = MODE_IDENTIFIER_TO_API_SLUG_MAP[modeIdentifier];

  if (!apiMode) {
    console.warn(`[Api.js] getHistoricalLogsByDate: No backend API slug mapping for mode identifier "${modeIdentifier}".`);
    return { items: [], total_count: 0, has_more: false, page: 0, page_size: limit, total_pages: 0 };
  }

  const encodedSourceId = encodeURIComponent(sourceId); // This should be camera_display_name
  
  let queryParams = new URLSearchParams();
  if (startTimeUtc && typeof startTimeUtc === 'string') {
    queryParams.append('startTimeUtc', startTimeUtc);
  }
  if (endTimeUtc && typeof endTimeUtc === 'string') {
    queryParams.append('endTimeUtc', endTimeUtc);
  }
  if (limit !== null && limit !== undefined) {
    queryParams.append('limit', String(limit));
  }
  if (skip !== null && skip !== undefined) {
    queryParams.append('skip', String(skip));
  }
  if (sortOrder) {
    queryParams.append('sortOrder', sortOrder);
  }

  const queryString = queryParams.toString();
  const requestUrl = `${API_BASE_URL}/logs/historical_by_date/${apiMode}/${encodedSourceId}${queryString ? `?${queryString}` : ''}`;
  
  console.log(`[Api.js] Fetching historical logs BY DATE: Identifier='${modeIdentifier}', API_Mode='${apiMode}', Source='${sourceId}', Dates: ${startTimeUtc}-${endTimeUtc}, Limit='${limit}', Skip='${skip}', SortOrder='${sortOrder}', URL='${requestUrl}'`);

  try {
    const response = await fetch(requestUrl, buildAuthFetchOptions());
    const data = await handleApiResponse(response, `Get Historical Logs for ${modeIdentifier} by Date`);
    
    // Validate response data structure
    if (!data) {
      console.warn(`[Api.js] getHistoricalLogsByDate: Received null/undefined response for ${modeIdentifier}`);
      return { items: [], total_count: 0, has_more: false, page: 0, page_size: limit, total_pages: 0 };
    }
    
    // Handle both new paginated response and legacy array response (backward compatibility)
    if (Array.isArray(data)) {
      console.log(`[Api.js] Received legacy array response for ${modeIdentifier} by date, converting to paginated format`);
      return {
        items: data,
        total_count: data.length,
        has_more: false,
        page: 0,
        page_size: data.length,
        total_pages: 1
      };
    }
    
    // Validate paginated response structure
    if (typeof data !== 'object') {
      console.warn(`[Api.js] getHistoricalLogsByDate: Invalid response type for ${modeIdentifier}, expected object`, typeof data);
      return { items: [], total_count: 0, has_more: false, page: 0, page_size: limit, total_pages: 0 };
    }
    
    // New paginated response format with fallbacks
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total_count: data.total_count || 0,
      has_more: data.has_more || false,
      page: data.page || 0,
      page_size: data.page_size || limit,
      total_pages: data.total_pages || 0
    };
  } catch (error) {
    // This catch block will now primarily handle network errors or issues if handleApiResponse itself fails unexpectedly
    console.error(`[Api.js] Network/Unhandled error fetching logs by date for API_Mode='${apiMode}', source='${sourceId}':`, error);
    return { items: [], total_count: 0, has_more: false, page: 0, page_size: limit, total_pages: 0 };
  }
};

export const getRegisteredCameras = async () => {
  const response = await fetch(
    `${API_BASE_URL}/cameras/registered`,
    buildAuthFetchOptions()
  );
  return handleApiResponse(response, "Get Registered Cameras");
};

export const getCameraDetails = async (params) => {
  const query = new URLSearchParams(params).toString();
  const response = await fetch(
    `${API_BASE_URL}/cameras/details?${query}`,
    buildAuthFetchOptions()
  );
  if (response.status === 404) { // Explicitly handle 404 for getCameraDetails as "not found" is a valid business case
      console.log(`[Api] Get Camera Details: Not found (404) for query: ${query}`);
      return null;
  }
  return handleApiResponse(response, "Get Camera Details");
};

export const registerCamera = async (cameraData) => {
  const response = await fetch(
    `${API_BASE_URL}/cameras/register`,
    buildAuthFetchOptions({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cameraData),
    })
  );
  return handleApiResponse(response, "Register Camera");
};

export const editCamera = async (cameraId, cameraData) => {
  const response = await fetch(
    `${API_BASE_URL}/cameras/${cameraId}`,
    buildAuthFetchOptions({
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cameraData),
    })
  );
  return handleApiResponse(response, "Edit Camera");
};

export const deleteCamera = async (cameraId) => {
  const response = await fetch(
    `${API_BASE_URL}/cameras/${cameraId}`,
    buildAuthFetchOptions({
      method: "DELETE",
    })
  );
  return handleApiResponse(response, "Delete Camera");
};

export const getConsolidatedAlerts = async (
  limit = 100,
  startTimeUtc = null,
  endTimeUtc = null,
  alertStatus = null,
  skip = 0,
  sortOrder = 'desc'
) => {
  const params = new URLSearchParams();
  if (limit !== null && limit !== undefined) params.append("limit", String(limit));
  if (skip !== null && skip !== undefined) params.append("skip", String(skip));
  if (startTimeUtc) params.append("startTimeUtc", startTimeUtc);   
  if (endTimeUtc)   params.append("endTimeUtc",   endTimeUtc);
  if (alertStatus)  params.append("alert_status", alertStatus);
  if (sortOrder) params.append("sortOrder", sortOrder);
  const qs  = params.toString();          
  const url = `${API_BASE_URL}/logs/consolidated_alerts${qs ? `?${qs}` : ""}`;
  
  console.log(`[Api.js] Fetching consolidated alerts: Limit='${limit}', Skip='${skip}', StartTime='${startTimeUtc}', EndTime='${endTimeUtc}', Status='${alertStatus}', SortOrder='${sortOrder}', URL='${url}'`);

  try {
    const data = await handleApiResponse(
      await fetch(url, buildAuthFetchOptions()),
      "Get Consolidated Alerts"
    );
    
    // Validate response data structure
    if (!data) {
      console.warn(`[Api.js] getConsolidatedAlerts: Received null/undefined response`);
      return { items: [], total_count: 0, has_more: false, page: 0, page_size: limit, total_pages: 0 };
    }
    
    // Handle both new paginated response and legacy array response (backward compatibility)
    if (Array.isArray(data)) {
      console.log(`[Api.js] Received legacy array response for consolidated alerts, converting to paginated format`);
      return {
        items: data,
        total_count: data.length,
        has_more: false,
        page: 0,
        page_size: data.length,
        total_pages: 1
      };
    }
    
    // Validate paginated response structure
    if (typeof data !== 'object') {
      console.warn(`[Api.js] getConsolidatedAlerts: Invalid response type, expected object`, typeof data);
      return { items: [], total_count: 0, has_more: false, page: 0, page_size: limit, total_pages: 0 };
    }
    
    // New paginated response format with fallbacks
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total_count: data.total_count || 0,
      has_more: data.has_more || false,
      page: data.page || 0,
      page_size: data.page_size || limit,
      total_pages: data.total_pages || 0
    };
  } catch (error) {
    console.error(`[Api.js] Error fetching consolidated alerts:`, error);
    return { items: [], total_count: 0, has_more: false, page: 0, page_size: limit, total_pages: 0 };
  }
};

export const getConsolidatedAlertsForAnalytics = async (
  startTimeUtc = null,
  endTimeUtc = null,
  sortOrder = 'desc'
) => {
  const params = new URLSearchParams();
  if (startTimeUtc) params.append("startTimeUtc", startTimeUtc);   
  if (endTimeUtc)   params.append("endTimeUtc",   endTimeUtc);
  if (sortOrder) params.append("sortOrder", sortOrder);
  const qs  = params.toString();          
  const url = `${API_BASE_URL}/logs/consolidated_alerts/analytics${qs ? `?${qs}` : ""}`;
  
  console.log(`[Api.js] Fetching consolidated alerts for analytics: StartTime='${startTimeUtc}', EndTime='${endTimeUtc}', SortOrder='${sortOrder}', URL='${url}'`);

  try {
    const data = await handleApiResponse(
      await fetch(url, buildAuthFetchOptions()),
      "Get Consolidated Alerts for Analytics"
    );
    
    // Handle both new paginated response and legacy array response (backward compatibility)
    if (Array.isArray(data)) {
      console.log(`[Api.js] Received legacy array response for analytics consolidated alerts, converting to paginated format`);
      return {
        items: data,
        total_count: data.length,
        has_more: false,
        page: 0,
        page_size: data.length,
        total_pages: 1
      };
    }
    
    // New paginated response format
    return {
      items: data.items || [],
      total_count: data.total_count || 0,
      has_more: data.has_more || false,
      page: data.page || 0,
      page_size: data.page_size || data.items?.length || 0,
      total_pages: data.total_pages || 0
    };
  } catch (error) {
    console.error(`[Api.js] Error fetching consolidated alerts for analytics:`, error);
    return { items: [], total_count: 0, has_more: false, page: 0, page_size: 0, total_pages: 0 };
  }
};

export const requestMicSlotForMode = async (
  cameraDisplayName,
  uiModeIdentifier = "Customer Service Monitor"   // default CSM
) =>
  requestWebSocketAudioSlot({
    camera_display_name: cameraDisplayName,
    analysis_mode: uiModeIdentifier,             // <- explicit & clear
  });
// ─────────────────────────────────────────────────────────────────────────
//  Remote-mic control (pairs with /mics/request_stream + /mics/stop_stream)
// ─────────────────────────────────────────────────────────────────────────
export const requestMicStream = async (cameraDisplayName) =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/mics/request_stream`,
      buildAuthFetchOptions({
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ camera_display_name: cameraDisplayName }),
      })
    ),
    `Request Mic Stream (${cameraDisplayName})`
  );

export const stopMicStream = async (cameraDisplayName) =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/mics/stop_stream`,
      buildAuthFetchOptions({
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ camera_display_name: cameraDisplayName }),
      })
    ),
    `Stop Mic Stream (${cameraDisplayName})`
  );

export const getAudioMetrics = async (
  limit = null,
  skip = 0,
  startTimeUtc = null,
  endTimeUtc = null
) => {
  const params = new URLSearchParams();
  if (limit !== null && limit !== undefined) params.append("limit", String(limit));
  if (skip !== null && skip !== undefined) params.append("skip", String(skip));
  if (startTimeUtc) params.append("startTimeUtc", startTimeUtc);
  if (endTimeUtc) params.append("endTimeUtc", endTimeUtc);
  
  const qs = params.toString();
  const url = `${API_BASE_URL}/logs/audio-metrics${qs ? `?${qs}` : ""}`;
  return handleApiResponse(
    await fetch(url, buildAuthFetchOptions()),
    "Get Audio Metrics"
  );
};

export const getGeminiTokens = async () => {
  const url = `${API_BASE_URL}/logs/gemini-tokens`;
  return handleApiResponse(
    await fetch(url, buildAuthFetchOptions()),
    "Get Gemini Tokens"
  );
};

// ─────────────────────────────────────────────────────────────────────────
//  Waiting Time Statistics API Functions
// ─────────────────────────────────────────────────────────────────────────
export const getWaitingTimeStatisticsCameras = async () =>
  handleApiResponse(
    await fetch(`${API_BASE_URL}/waiting-time/statistics/cameras`,
      buildAuthFetchOptions()
    ),
    "Get Waiting Time Statistics Cameras"
  );

export const getWaitingTimeStatistics = async (cameraId) => {
  if (!cameraId) {
    throw new Error("getWaitingTimeStatistics: cameraId is required");
  }
  return handleApiResponse(
    await fetch(`${API_BASE_URL}/waiting-time/statistics/${encodeURIComponent(cameraId)}`,
      buildAuthFetchOptions()
    ),
    `Get Waiting Time Statistics for ${cameraId}`
  );
};

export const invalidateWaitingTimeStatistics = async (cameraId) => {
  if (!cameraId) {
    throw new Error("invalidateWaitingTimeStatistics: cameraId is required");
  }
  return handleApiResponse(
    await fetch(`${API_BASE_URL}/waiting-time/statistics/${encodeURIComponent(cameraId)}/invalidate`,
      buildAuthFetchOptions({
        method: "POST",
      })
    ),
    `Invalidate Waiting Time Statistics Cache for ${cameraId}`
  );
};

export const getWaitingTimeStatisticsWebSocket = (cameraId) => {
  if (!cameraId) return null;

  const token = getAccessToken();
  if (!token) {
    console.warn('[Api] getWaitingTimeStatisticsWebSocket: No access token available');
    return null;
  }

  // Proper URL construction that handles localhost/127.0.0.1 and http/https
  const wsUrl = API_BASE_URL
    .replace(/^https?:\/\/127\.0\.0\.1/, 'ws://localhost')
    .replace(/^https/, 'wss')
    .replace(/^http/, 'ws');

  const finalUrl = `${wsUrl}/waiting-time/statistics/live/${encodeURIComponent(cameraId)}?token=${encodeURIComponent(token)}`;
  console.log(`[Api] Creating WebSocket connection to: ${finalUrl}`);

  return new WebSocket(finalUrl);
};

// ─────────────────────────────────────────────────────────────────────────
//  Helper: Get Access Token from localStorage
// ─────────────────────────────────────────────────────────────────────────
const getAccessToken = () => {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  const rawToken = localStorage.getItem('accessToken');
  if (!rawToken) {
    return null;
  }

  const trimmedToken = rawToken.trim();
  if (!trimmedToken || trimmedToken === 'null' || trimmedToken === 'undefined') {
    return null;
  }

  let cleanedToken = trimmedToken;
  if (cleanedToken.startsWith('Bearer ')) {
    cleanedToken = cleanedToken.slice('Bearer '.length).trim();
  }

  if (!cleanedToken || cleanedToken === 'null' || cleanedToken === 'undefined') {
    return null;
  }

  // If we had to clean the stored value, persist the sanitized token for future reads
  if (cleanedToken !== rawToken) {
    try {
      localStorage.setItem('accessToken', cleanedToken);
    } catch (error) {
      console.warn('[Api.js] Failed to persist cleaned access token:', error);
    }
  }

  return cleanedToken;
};

const buildAuthHeaders = (additionalHeaders = {}) => {
  const token = getAccessToken();
  if (!token) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[Api.js] Authenticated request attempted without access token');
    }
    return { ...additionalHeaders };
  }

  const authorization = `Bearer ${token}`;
  return {
    ...additionalHeaders,
    Authorization: authorization,
  };
};

const buildAuthFetchOptions = (options = {}) => {
  const { headers: extraHeaders = {}, ...rest } = options;
  return {
    ...rest,
    headers: buildAuthHeaders(extraHeaders),
  };
};
// ─────────────────────────────────────────────────────────────────────────
//  Organization Management API Functions
// ─────────────────────────────────────────────────────────────────────────

export const getOrganization = async (orgId) => {
  return handleApiResponse(
    await fetch(
      `${API_BASE_URL}/organizations/${orgId}`,
      buildAuthFetchOptions()
    ),
    "Get organization"
  );
};

export const updateOrganization = async (orgId, data) => {
  return handleApiResponse(
    await fetch(
      `${API_BASE_URL}/organizations/${orgId}`,
      buildAuthFetchOptions({
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
    ),
    "Update organization"
  );
};

export const listOrganizations = async () => {
  return handleApiResponse(
    await fetch(
      `${API_BASE_URL}/organizations`,
      buildAuthFetchOptions()
    ),
    "List organizations"
  );
};

export const listOrganizationUsers = async (orgId) => {
  return handleApiResponse(
    await fetch(
      `${API_BASE_URL}/organizations/${orgId}/users`,
      buildAuthFetchOptions()
    ),
    `List users in organization ${orgId}`
  );
};

// ─────────────────────────────────────────────────────────────────────────
//  Role Management API Functions
// ─────────────────────────────────────────────────────────────────────────

export const listRoles = async () => {
  return handleApiResponse(
    await fetch(
      `${API_BASE_URL}/organizations/roles`,
      buildAuthFetchOptions()
    ),
    "List roles"
  );
};

export const getRole = async (roleId) => {
  return handleApiResponse(
    await fetch(
      `${API_BASE_URL}/organizations/roles/${roleId}`,
      buildAuthFetchOptions()
    ),
    "Get role"
  );
};

// ─────────────────────────────────────────────────────────────────────────
//  User-Role Management API Functions
// ─────────────────────────────────────────────────────────────────────────

export const getUserRoles = async (userEmail) => {
  return handleApiResponse(
    await fetch(
      `${API_BASE_URL}/organizations/users/${encodeURIComponent(userEmail)}/roles`,
      buildAuthFetchOptions()
    ),
    `Get roles for user ${userEmail}`
  );
};

export const assignRoleToUser = async (data) => {
  return handleApiResponse(
    await fetch(
      `${API_BASE_URL}/organizations/users/roles`,
      buildAuthFetchOptions({
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
    ),
    "Assign role to user"
  );
};

export const revokeRoleFromUser = async (userEmail, roleId, organizationId) => {
  const params = new URLSearchParams({
    user_email: userEmail,
    role_id: roleId,
    organization_id: organizationId
  });
  return handleApiResponse(
    await fetch(
      `${API_BASE_URL}/organizations/users/roles?${params.toString()}`,
      buildAuthFetchOptions({
        method: "DELETE",
      })
    ),
    `Revoke role from user ${userEmail}`
  );
};

export const deleteUser = async (userEmail, organizationId) => {
  const params = new URLSearchParams({
    organization_id: organizationId
  });
  return handleApiResponse(
    await fetch(
      `${API_BASE_URL}/organizations/users/${encodeURIComponent(userEmail)}?${params.toString()}`,
      buildAuthFetchOptions({
        method: "DELETE",
      })
    ),
    `Delete user ${userEmail}`
  );
};

// ─────────────────────────────────────────────────────────────────────────
//  Invitation Management API Functions
// ─────────────────────────────────────────────────────────────────────────

export const createInvitation = async (orgId, data) => {
  return handleApiResponse(
    await fetch(
      `${API_BASE_URL}/organizations/${orgId}/invitations`,
      buildAuthFetchOptions({
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
    ),
    "Create invitation"
  );
};

export const listInvitations = async (orgId) => {
  return handleApiResponse(
    await fetch(
      `${API_BASE_URL}/organizations/${orgId}/invitations`,
      buildAuthFetchOptions()
    ),
    "List invitations"
  );
};

export const revokeInvitation = async (code) => {
  return handleApiResponse(
    await fetch(
      `${API_BASE_URL}/organizations/invitations/${code}`,
      buildAuthFetchOptions({
        method: "DELETE",
      })
    ),
    `Revoke invitation ${code}`
  );
};

export const validateInvitation = async (code) => {
  // No auth required for validation (public endpoint)
  return handleApiResponse(
    await fetch(`${API_BASE_URL}/organizations/invitations/${code}/validate`),
    `Validate invitation ${code}`
  );
};

// ─────────────────────────────────────────────────────────────────────────
//  User Profile API Functions
// ─────────────────────────────────────────────────────────────────────────

export const getUserProfile = async () => {
  return handleApiResponse(
    await fetch(
      `${API_BASE_URL}/auth/me`,
      buildAuthFetchOptions()
    ),
    "Get user profile"
  );
};

export const logout = async () => {
  const token = getAccessToken();
  if (!token) {
    console.warn('[Api] Logout requested but no access token found in storage.');
    return { message: 'Already logged out' };
  }

  return handleApiResponse(
    await fetch(
      `${API_BASE_URL}/auth/logout`,
      buildAuthFetchOptions({
        method: "POST",
      })
    ),
    "Logout"
  );
};
